import { SubMissionActorCaseEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import type { MissionForm_Mission } from '../fragments/mission.fragment';

export type MissionFormActor = NonNullable<
  MissionForm_Mission['internalActors']
>[number];

// The external quotation first, the usual case.
export const QUOTATION_CASES: readonly SubMissionActorCaseEnum[] = [
  SubMissionActorCaseEnum.SameEmitterAndReceiver,
  SubMissionActorCaseEnum.InternalRecharging,
];

export const QUOTATION_CASE_LABELS: Readonly<
  Record<SubMissionActorCaseEnum, string>
> = {
  [SubMissionActorCaseEnum.SameEmitterAndReceiver]: 'Devis externe',
  [SubMissionActorCaseEnum.InternalRecharging]: 'Devis interne',
};

// An external quotation sub-mission inherits the actors of its collaboration,
// its emitter of its own when the collaboration has several internal actors (a
// sub-mission is carried by one organization).
export const externalQuotationInternalActorsOf = (
  collaborationEmitter: Readonly<MissionFormActor> | undefined,
  hasSeveralInternalActors: boolean,
): readonly MissionFormActor[] | null =>
  hasSeveralInternalActors && collaborationEmitter
    ? [{ ...collaborationEmitter, index: 0 }]
    : null;

type QuotationCategoryIds = Readonly<{
  emitterCategoryId: Id;
  receiverCategoryId: Id;
}>;

const withQuotationRole = (
  actor: Readonly<MissionFormActor>,
  {
    roleCategoryId,
    quotationCategoryIds,
    index,
  }: Readonly<{
    roleCategoryId: Id;
    quotationCategoryIds: readonly Id[];
    index: number;
  }>,
): MissionFormActor => ({
  ...actor,
  index,
  categoryIds: [
    ...actor.categoryIds.filter(
      (categoryId) => !quotationCategoryIds.includes(categoryId),
    ),
    roleCategoryId,
  ],
});

// The EDM domain is carried by one organization of the internal list.
const withEdmDomain = (
  actor: Readonly<MissionFormActor>,
  useItsEdmDomain: boolean,
): MissionFormActor =>
  'useItsEdmDomain' in actor ? { ...actor, useItsEdmDomain } : actor;

// The lists of an internal quotation sub-mission: the chosen internal actor of
// its collaboration quotes the emitter of the collaboration, its external actor
// (a recharged internal). The chosen actor, its only internal one, carries the
// EDM domain.
export const buildInternalQuotationActors = ({
  emitter,
  collaborationEmitter,
  categoryIds: { emitterCategoryId, receiverCategoryId },
}: Readonly<{
  emitter: Readonly<MissionFormActor>;
  collaborationEmitter: Readonly<MissionFormActor>;
  categoryIds: QuotationCategoryIds;
}>): Readonly<{
  internalActors: readonly MissionFormActor[];
  externalActors: readonly MissionFormActor[];
}> => {
  const quotationCategoryIds = [emitterCategoryId, receiverCategoryId];

  return {
    internalActors: [
      withEdmDomain(
        withQuotationRole(emitter, {
          roleCategoryId: emitterCategoryId,
          quotationCategoryIds,
          index: 0,
        }),
        true,
      ),
    ],
    externalActors: [
      withEdmDomain(
        withQuotationRole(collaborationEmitter, {
          roleCategoryId: receiverCategoryId,
          quotationCategoryIds,
          index: 0,
        }),
        false,
      ),
    ],
  };
};
